"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/features/onboarding-checklist/components/SetupCompleteCelebrationModal";

import React, { useEffect } from "react";
import { usePathname } from "next/navigation";
import { useSnapshot } from "valtio";
import { Icon } from "@ospweb/sdk-ui";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { ROUTER_PATH } from "@config/router";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { EdgenModalCreateAble } from "@/src/components/EdgenModal/index";
import { callEdgenModalWhenRootReadyAsync } from "@/src/components/EdgenModal/waitForEdgenModalRoot";
import {
  SetupChecklistStore,
  confirmCelebrationDismiss,
} from "@/src/store/modules/setupChecklist";
import { CHECKLIST_STEP_META } from "../constants/stepMeta";
import {
  trackSetupCompleteView,
  trackSetupCompleteDismiss,
} from "../utils/tracking";

const I18n = I18nUtil.createScoped(Translations);

export interface SetupCompleteCelebrationContentProps {
  userId?: string;
  onClose?: () => void;
}

/**
 * S1.13: 起步清单全部达成庆祝页 — Figma "Setup complete"（#105443 PC / #104592 移动）
 * GoalLive 图 + headline + NowRunning 状态表 + Ed 节奏承诺 + Got it 终身持久化防重
 */
export const SetupCompleteCelebrationContent: React.FC<
  SetupCompleteCelebrationContentProps
> = ({ userId = "", onClose }) => {
  const checklistSnap = useSnapshot(SetupChecklistStore);
  const stepMetas = CHECKLIST_STEP_META;
  const router = useShellRouter();
  const pathname = usePathname();

  useEffect(() => {
    trackSetupCompleteView();
  }, []);

  const handleGotIt = async () => {
    trackSetupCompleteDismiss();
    try {
      await confirmCelebrationDismiss(userId);
    } catch {
      // 容错降级
    } finally {
      onClose?.();
      // 完成页是收尾，Got it 后回到 Brief（EDG-559）：最后一项多半是激活 goal，用户此时停在 goal 页，
      // 只关弹窗会把人留在那里。已经在 Brief 就不重复跳转；壳内由 useShellRouter 按壳规则分流
      if (pathname !== ROUTER_PATH.BRIEF) {
        router.push(ROUTER_PATH.BRIEF);
      }
    }
  };

  const ffScore = checklistSnap.data?.ffScore ?? 100;

  return (
    <div
      data-testid="setup-complete-celebration-modal"
      data-openreplay-label="modal_setup_complete_celebration"
      className="flex h-[100dvh] w-screen flex-col bg-background_solid_secondary"
    >
      <div className="flex-1 overflow-y-auto">
        {/* 移动 Content padding-top 40（#104592）；PC Body 80 + Content 40 = 120（#105443） */}
        <div className="mx-auto w-full max-w-[740px] px-4 pt-10 md:pt-[120px]">
          <div className="flex flex-col gap-[22px]">
            {/* 庆祝图标（SVG 自带 123×96 视口，保持原始宽高比） */}
            <div className="flex justify-center" aria-hidden="true">
              <OspIcons name="icon-goallive" />
            </div>

            {/* 标题与副标题 */}
            <div className="flex flex-col gap-2 text-center">
              <h2 className="headline_regular_s text-content_primary">
                {I18n.t("I can see the whole picture now.")}
              </h2>
              <p className="body_m text-content_tertiary">
                {I18n.t(
                  "Six pieces connected. Nothing about your money is outside the watch any more.",
                )}
              </p>
            </div>

            {/* NowRunning 状态卡（6 项全量） */}
            <div className="flex flex-col gap-2 rounded-xl bg-background_solid_neutral_primary pb-1 pt-4">
              <p className="body_medium_s px-4 text-center text-content_tertiary">
                {I18n.t("What's now running")}
              </p>
              <div className="flex flex-col">
                {stepMetas.map((meta, index) => (
                  <div
                    key={meta.stepId}
                    className={`flex items-center gap-2 px-4 py-3 ${
                      index < stepMetas.length - 1
                        ? "border-b border-border_inverse_transparent10"
                        : ""
                    }`}
                  >
                    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-background_accent1_overlay100">
                      <Icon name="check" size={12} className="text-white" />
                    </span>
                    <span className="body_bold_s flex-1 text-content_primary">
                      {meta.runningName}
                    </span>
                    {meta.stepKey === "ff_score" ? (
                      <span className="body_medium_s text-content_brand_primary">
                        {`${ffScore}/100`}
                      </span>
                    ) : meta.stepKey === "notification" ? (
                      <span className="body_medium_s text-content_brand_primary">
                        {meta.runningLabel}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <span className="size-1.5 rounded-full bg-background_accent1_overlay100" />
                        <span className="body_medium_s text-content_brand_primary">
                          {meta.runningLabel}
                        </span>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Ed 节奏承诺脚注 */}
            <p className="body_s text-center text-content_quaternary">
              {I18n.t(
                "Your first read lands tomorrow morning. After that I check every day, tell you the same day something moves, and send a short read every two weeks.",
              )}
            </p>
          </div>
        </div>
      </div>

      {/* 底部 CTA：Got it（移动 BottomCTA 12/16/24；PC 与正文间距 24） */}
      <div className="shrink-0 px-4 pb-6 pt-3 md:pt-6">
        <div className="mx-auto w-full max-w-[740px]">
          <button
            type="button"
            data-testid="celebration-got-it-button"
            data-openreplay-label="checklist_celebration_got_it"
            onClick={handleGotIt}
            className="flex h-12 w-full items-center justify-center rounded-lg bg-background_brand_overlay100 text-white button_l"
          >
            {I18n.t("Got it")}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * 命令式呼出 6/6 完成庆祝页（全屏接管）。
 * 经 waitForEdgenModalRoot 重试：首屏 fetchChecklist 命中 showCelebration 时，
 * lazyHydrate 的 EdgenModalCreateAble.Root 可能尚未挂载，直接 call 会抛
 * `No <Root> found` 且 GlobalManager 的 finally 会吞掉本次庆祝 —— 必须等 Root 就绪。
 */
export const openSetupCompleteCelebrationModal = (
  userId = "",
): Promise<boolean> =>
  callEdgenModalWhenRootReadyAsync(() =>
    EdgenModalCreateAble.call({
      children: (
        <SetupCompleteCelebrationContent
          userId={userId}
          onClose={() => {
            EdgenModalCreateAble.end(false);
          }}
        />
      ),
      onCancel: () => {
        void confirmCelebrationDismiss(userId);
        EdgenModalCreateAble.end(false);
      },
      destroyOnClose: true,
      // 全屏壳层方案同 openSetupChecklistSheetModal：OspUI 将 .ant-modal 钉为 fit-content !important，
      // 由内容根节点 w-screen + 100dvh 撑满视口。
      className: "!m-0 !max-w-none !p-0",
      containerClassName: "bg-background_solid_secondary",
      contentStyle: {
        borderRadius: 0,
        width: "fit-content",
        maxWidth: "100vw",
        height: "auto",
        maxHeight: "100dvh",
        padding: 0,
        overflow: "hidden",
      },
      containerStyle: {
        width: "fit-content",
        height: "fit-content",
        maxWidth: "100vw",
        maxHeight: "100dvh",
        overflow: "hidden",
      },
      showClose: false,
      styles: {
        wrapper: {
          zIndex: 10000,
        },
        mask: {
          zIndex: 10000,
        },
      },
    }),
  ).catch(() => false);
